(function () {
    'use strict';

    const script = document.currentScript;
    const config = {
        endpoint: script && script.dataset.signalEndpoint,
        brandCode: script && script.dataset.signalBrand,
        publicKey: script && script.dataset.signalKey,
        enabled: Boolean(script && script.dataset.signalEnabled === 'true'),
        pageFamily: (script && script.dataset.signalPageFamily) || document.body.dataset.signalPageFamily || 'auto',
        consentMode: (script && script.dataset.signalConsentMode) || 'shopify'
    };
    const STORAGE = {
        visitor: '_signal_visitor_v1',
        session: '_signal_session_v1',
        attribution: '_signal_attribution_v1',
        errors: '_signal_errors_v1'
    };
    const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
    const BATCH_SIZE = 20;
    const FLUSH_MS = 5000;
    const queue = [];
    const rageWindows = new Map();
    const seenScroll = new Set();
    const startedForms = new WeakSet();
    let started = false;
    let flushTimer = null;
    let mutationVersion = 0;
    let consent = { analytics: false, marketing: false };

    function uuid() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (character) {
            const random = Math.random() * 16 | 0;
            const value = character === 'x' ? random : (random & 0x3 | 0x8);
            return value.toString(16);
        });
    }

    function safeJSONParse(value, fallback) {
        try { return JSON.parse(value); } catch (_error) { return fallback; }
    }

    function getStorage(name) {
        try { return window.localStorage.getItem(name); } catch (_error) { return null; }
    }

    function setStorage(name, value) {
        try { window.localStorage.setItem(name, value); } catch (_error) { /* fail closed */ }
    }

    function clearIdentity() {
        try {
            window.localStorage.removeItem(STORAGE.visitor);
            window.localStorage.removeItem(STORAGE.session);
            window.localStorage.removeItem(STORAGE.attribution);
            window.localStorage.removeItem(STORAGE.errors);
        } catch (_error) { /* nothing else to clear */ }
        ['_signal_vid', '_signal_sid', '_signal_attr'].forEach(function (name) {
            try { document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax; Secure`; } catch (_error) { /* no-op */ }
        });
    }

    function setCookie(name, value, maxAgeSeconds) {
        try {
            document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax; Secure`;
        } catch (_error) { /* checkout bridge is optional */ }
    }

    function privacySnapshot() {
        try {
            if (config.consentMode === 'google') {
                const consentState = window.google_tag_data && window.google_tag_data.ics;
                if (!consentState || typeof consentState.getConsentState !== 'function') {
                    return { analytics: false, marketing: false };
                }
                return {
                    analytics: consentState.getConsentState('analytics_storage') === 'granted',
                    marketing: consentState.getConsentState('ad_storage') === 'granted'
                };
            }
            if (config.consentMode === 'custom') {
                const supplied = window.signalConsent || {};
                return { analytics: supplied.analytics === true, marketing: supplied.marketing === true };
            }
            const api = window.Shopify && window.Shopify.customerPrivacy;
            if (!api || typeof api.analyticsProcessingAllowed !== 'function') {
                return { analytics: false, marketing: false };
            }
            return {
                analytics: api.analyticsProcessingAllowed() === true,
                marketing: typeof api.marketingAllowed === 'function' && api.marketingAllowed() === true
            };
        } catch (_error) {
            return { analytics: false, marketing: false };
        }
    }

    function currentIdentity() {
        let visitorId = getStorage(STORAGE.visitor);
        if (!visitorId) {
            visitorId = uuid();
            setStorage(STORAGE.visitor, visitorId);
        }
        const now = Date.now();
        let session = safeJSONParse(getStorage(STORAGE.session), null);
        if (!session || !session.id || !session.lastSeen || now - session.lastSeen > SESSION_TIMEOUT_MS) {
            session = { id: uuid(), lastSeen: now };
        } else {
            session.lastSeen = now;
        }
        setStorage(STORAGE.session, JSON.stringify(session));
        setCookie('_signal_vid', visitorId, 90 * 86400);
        setCookie('_signal_sid', session.id, 1800);
        return { visitorId, sessionId: session.id };
    }

    function attribution() {
        if (!consent.marketing) return {};
        const parsed = safeJSONParse(getStorage(STORAGE.attribution), {}) || {};
        const existing = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        const params = new URLSearchParams(window.location.search);
        const mapping = {
            utm_source: 'source', utm_medium: 'medium', utm_campaign: 'campaign',
            utm_term: 'term', utm_content: 'content', gclid: 'gclid', fbclid: 'fbclid', ttclid: 'ttclid'
        };
        Object.keys(mapping).forEach(function (queryKey) {
            const value = params.get(queryKey);
            if (value) existing[mapping[queryKey]] = value.slice(0, 160);
        });
        setStorage(STORAGE.attribution, JSON.stringify(existing));
        setCookie('_signal_attr', JSON.stringify(existing), 90 * 86400);
        return existing;
    }

    function routeFamily() {
        const segments = window.location.pathname.split('/').filter(Boolean);
        if (segments[0] === 'products' && segments[1]) return '/products/:handle';
        if (segments[0] === 'collections' && segments[1]) return '/collections/:handle';
        if (segments[0] === 'pages' && segments[1]) return '/pages/:handle';
        if (segments[0] === 'blogs' && segments.length > 2) return '/blogs/:blog/:article';
        return '/' + segments.slice(0, 3).join('/');
    }

    function pageFamily() {
        if (config.pageFamily !== 'auto') return config.pageFamily;
        const path = window.location.pathname;
        if (/^\/products\//.test(path)) return 'product';
        if (/^\/collections\//.test(path)) return 'collection';
        if (/^\/cart\/?$/.test(path)) return 'cart';
        if (/apply|pre-approv|finance|lease|rent-to-own|buy-back/i.test(path)) return 'application';
        if (/contact|enquir|book/i.test(path)) return 'contact';
        return path === '/' ? 'home' : 'content';
    }

    function deviceClass() {
        const width = Math.max(window.screen && window.screen.width || 0, window.innerWidth || 0);
        if (width <= 767) return 'mobile';
        if (width <= 1024) return 'tablet';
        return 'desktop';
    }

    function componentContext(element) {
        const target = element && element.closest && element.closest('[data-signal-component]');
        if (!target) return null;
        return {
            element: target,
            component_id: target.dataset.signalComponent,
            region: target.dataset.signalRegion || null,
            action: target.dataset.signalAction || null
        };
    }

    function enqueue(eventName, contextOverrides, properties) {
        consent = privacySnapshot();
        if (!started || !consent.analytics) return;
        const identity = currentIdentity();
        const context = Object.assign({
            page_family: pageFamily(),
            route_family: routeFamily(),
            device_class: deviceClass()
        }, contextOverrides || {});
        queue.push({
            schema_version: 1,
            event_id: uuid(),
            event_name: eventName,
            brand_code: config.brandCode,
            visitor_id: identity.visitorId,
            session_id: identity.sessionId,
            occurred_at: new Date().toISOString(),
            consent: consent,
            context: context,
            attribution: attribution(),
            properties: properties || {}
        });
        if (queue.length >= BATCH_SIZE) flush();
    }

    function flush() {
        consent = privacySnapshot();
        if (!consent.analytics || queue.length === 0) {
            if (!consent.analytics) queue.length = 0;
            return;
        }
        const events = queue.splice(0, BATCH_SIZE);
        window.fetch(config.endpoint, {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            keepalive: true,
            headers: { 'Content-Type': 'application/json', 'X-Signal-Key': config.publicKey },
            body: JSON.stringify({ events: events })
        }).catch(function () { /* storefront behaviour must never depend on telemetry */ });
    }

    function trackClick(event) {
        const component = componentContext(event.target);
        if (!component || !component.component_id) return;
        const context = {
            component_id: component.component_id,
            region: component.region,
            action: component.action
        };
        enqueue('click', context, {});

        const now = Date.now();
        const recent = (rageWindows.get(component.component_id) || []).filter(function (time) { return now - time <= 2000; });
        recent.push(now);
        rageWindows.set(component.component_id, recent);
        if (recent.length === 3) enqueue('rage_click', context, { click_count: 3, interval_ms: 2000 });

        if (component.element.dataset.signalDeadcheck === 'true') {
            const versionAtClick = mutationVersion;
            const pathAtClick = window.location.pathname;
            window.setTimeout(function () {
                if (mutationVersion === versionAtClick && window.location.pathname === pathAtClick && component.element.isConnected) {
                    enqueue('dead_click', context, { wait_ms: 1200 });
                }
            }, 1200);
        }
    }

    function trackForms(event) {
        const form = event.target && event.target.closest && event.target.closest('[data-signal-form]');
        if (!form) return;
        const formId = form.dataset.signalForm;
        const componentId = form.dataset.signalComponent || `form.${formId}`;
        let eventName = 'form_start';
        let properties = { form_id: formId };
        if (event.type === 'submit') {
            eventName = 'form_submit';
        } else if (event.type === 'invalid') {
            eventName = 'form_error';
            properties = { form_id: formId, error_code: 'validation_error' };
        } else if (startedForms.has(form)) {
            return;
        } else {
            startedForms.add(form);
        }
        enqueue(eventName, {
            component_id: componentId,
            region: form.dataset.signalRegion || 'form',
            action: event.type
        }, properties);
    }

    function shouldTrackError(code) {
        const identity = currentIdentity();
        const parsed = safeJSONParse(getStorage(STORAGE.errors), null);
        const state = parsed && parsed.session_id === identity.sessionId && Array.isArray(parsed.codes)
            ? parsed
            : { session_id: identity.sessionId, codes: [] };
        if (state.codes.includes(code) || state.codes.length >= 20) return false;
        state.codes.push(code);
        setStorage(STORAGE.errors, JSON.stringify(state));
        return true;
    }

    function installScrollTracking() {
        window.addEventListener('scroll', function () {
            const root = document.documentElement;
            const denominator = Math.max(1, root.scrollHeight - window.innerHeight);
            const percent = Math.min(100, Math.round(100 * window.scrollY / denominator));
            [25, 50, 75, 90].forEach(function (milestone) {
                if (percent >= milestone && !seenScroll.has(milestone)) {
                    seenScroll.add(milestone);
                    enqueue('scroll_depth', {}, { percent: milestone });
                }
            });
        }, { passive: true });
    }

    function installVisibilityTracking() {
        if (!window.IntersectionObserver) return;
        const visibleSince = new WeakMap();
        const observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    visibleSince.set(entry.target, performance.now());
                } else if (visibleSince.has(entry.target)) {
                    const startedAt = visibleSince.get(entry.target);
                    visibleSince.delete(entry.target);
                    const component = componentContext(entry.target);
                    const duration = Math.round(performance.now() - startedAt);
                    if (component && duration >= 1000) {
                        enqueue('element_visible', {
                            component_id: component.component_id,
                            region: component.region,
                            action: component.action
                        }, { duration_ms: duration });
                    }
                }
            });
        }, { threshold: [0, 0.5] });
        document.querySelectorAll('[data-signal-component][data-signal-track-visibility="true"]').forEach(function (element) {
            observer.observe(element);
        });
    }

    function installErrorTracking() {
        window.addEventListener('error', function (event) {
            const file = event.filename ? event.filename.split('/').pop().split('?')[0] : 'inline';
            const errorName = event.error && event.error.name || 'Error';
            const code = `${errorName}:${file}:${event.lineno || 0}:${event.colno || 0}`.slice(0, 160);
            if (shouldTrackError(code)) enqueue('js_error', {}, { error_code: code });
        });
    }

    function installWebVitals() {
        if (!window.PerformanceObserver) return;
        try {
            new PerformanceObserver(function (list) {
                const entries = list.getEntries();
                const last = entries[entries.length - 1];
                if (last) enqueue('web_vital', {}, {
                    metric: 'LCP', value: last.startTime,
                    rating: last.startTime > 4000 ? 'poor' : (last.startTime > 2500 ? 'needs-improvement' : 'good')
                });
            }).observe({ type: 'largest-contentful-paint', buffered: true });
        } catch (_error) { /* unsupported observer types are omitted */ }
        let cls = 0;
        try {
            new PerformanceObserver(function (list) {
                list.getEntries().forEach(function (entry) { if (!entry.hadRecentInput) cls += entry.value; });
            }).observe({ type: 'layout-shift', buffered: true });
            window.addEventListener('pagehide', function () {
                enqueue('web_vital', {}, { metric: 'CLS', value: cls, rating: cls > 0.25 ? 'poor' : (cls > 0.1 ? 'needs-improvement' : 'good') });
                flush();
            });
        } catch (_error) { /* unsupported observer types are omitted */ }
    }

    function start() {
        if (started || !config.enabled || !config.endpoint || !config.brandCode || !config.publicKey) return;
        consent = privacySnapshot();
        if (!consent.analytics) return;
        started = true;
        new MutationObserver(function () { mutationVersion += 1; }).observe(document.documentElement, { childList: true, subtree: true, attributes: true });
        document.addEventListener('click', trackClick, true);
        document.addEventListener('focusin', trackForms, true);
        document.addEventListener('submit', trackForms, true);
        document.addEventListener('invalid', trackForms, true);
        document.addEventListener('signal:form_started', function (event) {
            const detail = event.detail || {};
            enqueue('form_start', {
                component_id: 'lead.form', region: 'form', action: 'start'
            }, { form_id: String(detail.form_id || 'lead_form').slice(0, 120) });
        });
        document.addEventListener('signal:form_error', function (event) {
            const detail = event.detail || {};
            enqueue('form_error', {
                component_id: 'lead.form', region: 'form', action: 'error'
            }, {
                form_id: String(detail.form_id || 'lead_form').slice(0, 120),
                error_code: String(detail.error_code || 'validation_error').slice(0, 120)
            });
        });
        document.addEventListener('signal:booking_completed', function (event) {
            const detail = event.detail || {};
            enqueue('booking_completed', { component_id: 'booking.form', region: 'booking', action: 'complete' }, {
                booking_id_hash: detail.booking_id,
                service_code: detail.service_code,
                value: detail.value,
                currency: detail.currency
            });
        });
        document.addEventListener('signal:form_completed', function (event) {
            const detail = event.detail || {};
            enqueue('form_submit', {
                component_id: 'lead.form', region: 'form', action: 'complete'
            }, { form_id: String(detail.form_id || 'lead_form').slice(0, 120) });
        });
        installScrollTracking();
        installVisibilityTracking();
        installErrorTracking();
        installWebVitals();
        enqueue('page_view', {}, {});
        flushTimer = window.setInterval(flush, FLUSH_MS);
        window.addEventListener('pagehide', flush);
    }

    function consentChanged() {
        const updated = privacySnapshot();
        if (!updated.analytics) {
            queue.length = 0;
            clearIdentity();
            if (flushTimer) window.clearInterval(flushTimer);
            flushTimer = null;
            started = false;
            return;
        }
        start();
    }

    if (!config.enabled) return;
    document.addEventListener('shopifyCustomerPrivacyApiLoaded', consentChanged);
    document.addEventListener('visitorConsentCollected', consentChanged);
    document.addEventListener('signal:consent_changed', consentChanged);
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
        start();
    }
})();
